import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { DocumentService } from './document.service';
import { LogicielCaisseStockReceptionService } from '../logiciel-caisse/logiciel-caisse-stock-reception.service';
import {
  LogicielCaisseStockTransfertService,
  StockTransfertCreateBody,
} from '../logiciel-caisse/logiciel-caisse-stock-transfert.service';
import { STOCK_TRANSFER_WORKFLOW_TYPE_IDS } from './stock-transfer-workflow.constants';

export type PvEcartDocumentItemForRegularisation = {
  type?: string;
  designation?: string;
  description?: string;
  quantity?: number;
  qnt?: number;
  code_barre?: string;
  code_bar?: string;
  reference?: string;
  ref_interne?: string;
  ref_fabricant?: string;
  sens?: string;
};

export type PvEcartRegularisationStockLigne = {
  sens: 'MANQUANT' | 'EXCEDENT';
  qnt: number;
  code_bar: string | null;
  ref_interne: string | null;
  ref_fabricant: string | null;
  reference: string | null;
};

export type ApplyPvEcartOnValideResult = {
  lcTransfertId: number;
  lcNumDocument: string | null;
  stockUpdated: number;
  stockAlreadyApplied?: boolean;
  /** @deprecated Réservations gérées à la création du transfert LC. */
  stockRegularisationSkipped?: boolean;
  /** Réservation disponible appliquée côté LC (création transfert). */
  stockReservationsOnTransfert?: boolean;
};

/** Query params pour ouvrir le transfert LC créé depuis un PV validé. */
export function buildPvEcartTransfertEntrepotNouveauQueryParams(
  lcTransfertId: number,
  erpPvDocumentId?: number | null
): Record<string, string> {
  const q: Record<string, string> = {
    lcTransfertId: String(lcTransfertId),
    return: '/transfert-entrepot/tous',
    from_pv_ecart: '1',
  };
  const pvId = Number(erpPvDocumentId);
  if (Number.isFinite(pvId) && pvId > 0) {
    q.pv_ecart_id = String(pvId);
  }
  return q;
}

function parseHiddenRecord(raw: unknown): Record<string, unknown> {
  if (!raw || typeof raw !== 'object') {
    return {};
  }
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw) as unknown;
      return p && typeof p === 'object' ? (p as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }
  return { ...(raw as Record<string, unknown>) };
}

export function readMergedDocumentHidden(
  doc: Record<string, unknown> | null | undefined
): Record<string, unknown> {
  if (!doc) {
    return {};
  }
  const client = parseHiddenRecord(doc['hidden_client_fields']);
  const fournisseur = parseHiddenRecord(doc['hidden_fournisseur_fields']);
  return { ...fournisseur, ...client };
}

export function isRegularisationStockApplied(
  doc: Record<string, unknown> | null | undefined
): boolean {
  const hidden = readMergedDocumentHidden(doc ?? null);
  return (
    hidden['regularisation_stock_applied'] === true ||
    hidden['regularisation_stock_applied'] === 1 ||
    String(hidden['regularisation_stock_applied'] ?? '').toLowerCase() === 'true'
  );
}

export function readPvEcartLcTransfertIdFromDoc(
  doc: Record<string, unknown> | null | undefined
): number | null {
  const hidden = readMergedDocumentHidden(doc ?? null);
  for (const key of [
    'pv_ecart_regularisation_lc_transfert_id',
    'logiciel_caisse_transfert_id',
    'stock_transfert_id',
  ]) {
    const n = Number(hidden[key]);
    if (Number.isFinite(n) && n > 0) {
      return n;
    }
  }
  return null;
}

function normalizePvEcartTextHint(value: unknown): string {
  return String(value ?? '')
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function inferSensFromPvLineText(it: PvEcartDocumentItemForRegularisation): 'MANQUANT' | 'EXCEDENT' | null {
  const blob = normalizePvEcartTextHint(
    [it.sens, it.description, it.designation].filter(Boolean).join(' ')
  );
  if (
    blob.includes('INFERIEURE') ||
    blob.includes('QTE RECUE INFERIEURE') ||
    blob.includes('QUANTITE RECUE INFERIEURE')
  ) {
    return 'MANQUANT';
  }
  if (
    blob.includes('SUPERIEURE') ||
    blob.includes('QTE RECUE SUPERIEURE') ||
    blob.includes('QUANTITE RECUE SUPERIEURE')
  ) {
    return 'EXCEDENT';
  }
  return null;
}

function resolveLineSensAndMagnitude(it: PvEcartDocumentItemForRegularisation): {
  sens: 'MANQUANT' | 'EXCEDENT';
  magnitude: number;
} | null {
  const rawQ = Math.trunc(Number(it.quantity ?? it.qnt) || 0);
  if (rawQ === 0) {
    return null;
  }
  const sensRaw = normalizePvEcartTextHint(it.sens);
  if (sensRaw === 'EXCEDENT') {
    return { sens: 'EXCEDENT', magnitude: Math.abs(rawQ) };
  }
  if (sensRaw === 'MANQUANT') {
    return { sens: 'MANQUANT', magnitude: Math.abs(rawQ) };
  }
  const fromText = inferSensFromPvLineText(it);
  if (fromText) {
    return { sens: fromText, magnitude: Math.abs(rawQ) };
  }
  if (rawQ < 0) {
    return { sens: 'EXCEDENT', magnitude: Math.abs(rawQ) };
  }
  return { sens: 'MANQUANT', magnitude: rawQ };
}

/** Lignes stock LC à partir des lignes PV (UI ou ERP). */
export function buildStockLignesFromPvDocumentItems(
  items: PvEcartDocumentItemForRegularisation[] | null | undefined
): PvEcartRegularisationStockLigne[] {
  const out: PvEcartRegularisationStockLigne[] = [];
  for (const it of items || []) {
    if (it.type && it.type !== 'product') {
      continue;
    }
    const resolved = resolveLineSensAndMagnitude(it);
    if (!resolved || resolved.magnitude <= 0) {
      continue;
    }
    const code_bar = String(it.code_barre ?? it.code_bar ?? '').trim() || null;
    const ref_interne = String(it.reference ?? it.ref_interne ?? '').trim() || null;
    const ref_fabricant = String(it.ref_fabricant ?? '').trim() || null;
    if (!code_bar && !ref_interne && !ref_fabricant) {
      continue;
    }
    out.push({
      sens: resolved.sens,
      qnt: resolved.magnitude,
      code_bar,
      ref_interne,
      ref_fabricant,
      reference: ref_interne,
    });
  }
  return out;
}

/** POST stock-transferts LC : quantités signées (+ manquant, − excédent). */
export function buildLcPvEcartTransfertBody(
  departLc: number,
  destLc: number,
  items: PvEcartDocumentItemForRegularisation[],
  note?: string | null
): StockTransfertCreateBody | null {
  if (!departLc || !destLc || departLc === destLc) {
    return null;
  }
  const lignes: StockTransfertCreateBody['lignes'] = [];
  for (const it of items || []) {
    if (it.type && it.type !== 'product') {
      continue;
    }
    const designation = String(it.designation ?? '').trim();
    if (!designation) {
      continue;
    }
    const resolved = resolveLineSensAndMagnitude(it);
    if (!resolved || resolved.magnitude <= 0) {
      continue;
    }
    const signedQ =
      resolved.sens === 'EXCEDENT' ? -resolved.magnitude : resolved.magnitude;
    const refLine =
      it.reference != null && String(it.reference).trim() !== ''
        ? String(it.reference).trim()
        : '-';
    const cb = String(it.code_barre ?? it.code_bar ?? '').trim();
    lignes.push({
      libelle_article: designation,
      reference: refLine,
      allocations: [
        {
          product_id: null,
          code_barre: cb || null,
          quantite: signedQ,
          selected: true,
        },
      ],
    });
  }
  if (!lignes.length) {
    return null;
  }
  return {
    date_transfert: new Date().toISOString(),
    entrepot_id_depart: departLc,
    entrepot_id_reception: destLc,
    note: (note ?? '').trim() || null,
    origine: 'manuelle',
    from_pv_ecart: true,
    lignes,
  };
}

export type PvEcartUiLineForLcFullSave = {
  type?: string;
  designation?: string;
  quantity?: number;
  code_barre?: string | null;
  reference?: string | null;
  product_id?: number | null;
  couleur?: string | null;
  taille?: string | null;
};

export type PvEcartUiBreakdownForLcFullSave = {
  qteEnvoyee: number;
  qteRecue: number;
  ecartSigned: number;
};

/**
 * PUT stock-transfert après approbation PV : toutes les variantes (envoyée / reçue / écart),
 * pas seulement les lignes d’écart — relecture approve-vente alignée sur document.component.
 */
export function buildLcPvEcartTransfertBodyFromUiFullBreakdown(
  departLc: number,
  destLc: number,
  rows: Array<{
    item: PvEcartUiLineForLcFullSave;
    breakdown?: PvEcartUiBreakdownForLcFullSave | null;
  }>,
  note?: string | null
): StockTransfertCreateBody | null {
  if (!departLc || !destLc || departLc === destLc) {
    return null;
  }
  const lignes: StockTransfertCreateBody['lignes'] = [];
  for (const { item, breakdown } of rows || []) {
    if (item.type && item.type !== 'product') {
      continue;
    }
    const designation = String(item.designation ?? '').trim();
    if (!designation) {
      continue;
    }
    const qEnv = Math.max(0, Math.trunc(Number(breakdown?.qteEnvoyee) || 0));
    const qRec = Math.max(0, Math.trunc(Number(breakdown?.qteRecue) || 0));
    const ecartSigned = Number.isFinite(Number(breakdown?.ecartSigned))
      ? Math.trunc(Number(breakdown?.ecartSigned))
      : qRec - qEnv;
    const qSigned = Math.trunc(Number(item.quantity) || 0);
    if (qEnv === 0 && qRec === 0 && qSigned === 0 && ecartSigned === 0) {
      continue;
    }
    const signedQ = qSigned !== 0 ? qSigned : ecartSigned;
    const refLine =
      item.reference != null && String(item.reference).trim() !== ''
        ? String(item.reference).trim()
        : '-';
    const cb = String(item.code_barre ?? '').trim();
    const pidRaw = item.product_id != null ? Number(item.product_id) : NaN;
    lignes.push({
      libelle_article: designation,
      reference: refLine,
      allocations: [
        {
          product_id: Number.isFinite(pidRaw) && pidRaw > 0 ? pidRaw : null,
          code_barre: cb || null,
          quantite: signedQ,
          quantite_envoyee: qEnv,
          quantite_recue: qRec,
          couleur: item.couleur ?? null,
          taille: item.taille ?? null,
          selected: true,
        },
      ],
    });
  }
  if (!lignes.length) {
    return null;
  }
  return {
    date_transfert: new Date().toISOString(),
    entrepot_id_depart: departLc,
    entrepot_id_reception: destLc,
    note: (note ?? '').trim() || null,
    origine: 'manuelle',
    from_pv_ecart: true,
    lignes,
  };
}

/** Détail lignes LC pour PUT pv-ecart-transfert-approuve-disponible (sync STLD). */
export function flattenLcStockTransfertBodyToPvApproveDetailLignes(
  body: StockTransfertCreateBody | null | undefined
): Array<Record<string, unknown>> {
  const out: Array<Record<string, unknown>> = [];
  for (const lg of body?.lignes || []) {
    const lib = String(lg.libelle_article ?? '').trim();
    const ref = String(lg.reference ?? '').trim();
    for (const alloc of lg.allocations || []) {
      const qEnvRaw = (alloc as { quantite_envoyee?: number }).quantite_envoyee;
      const qRecRaw = (alloc as { quantite_recue?: number }).quantite_recue;
      out.push({
        libelle: lib || null,
        designation: lib || null,
        reference: ref && ref !== '-' ? ref : null,
        code_barre: alloc.code_barre ?? null,
        product_id: alloc.product_id ?? null,
        quantite: Math.trunc(Number(alloc.quantite) || 0),
        quantite_envoyee: Math.trunc(Number(qEnvRaw) || 0),
        quantite_recue: Math.trunc(Number(qRecRaw) || 0),
        couleur: (alloc as { couleur?: string | null }).couleur ?? null,
        taille: (alloc as { taille?: string | null }).taille ?? null,
      });
    }
  }
  return out;
}

export function readPvEcartLignesFromErpDoc(
  doc: Record<string, unknown> | null | undefined
): Array<Record<string, unknown>> {
  if (!doc) {
    return [];
  }
  const raw = doc['lignesDocument'] ?? doc['lignes_document'];
  return Array.isArray(raw) ? (raw as Array<Record<string, unknown>>) : [];
}

/** Lignes produit PV ERP → items LC (même logique qu’à la validation). */
export function resolvePvEcartItemsForLcTransfertFromErpDoc(
  doc: Record<string, unknown> | null | undefined
): PvEcartDocumentItemForRegularisation[] {
  return mapErpLignesToPvRegularisationItems(readPvEcartLignesFromErpDoc(doc));
}

export type PvEcartTransfertUiLine = {
  designation: string;
  quantity: number;
  reference: string;
  code_barre: string;
  product_id: number | null;
  description: string;
};

/** Lignes affichées / enregistrées sur le transfert entrepôt (= PV, quantités signées). */
export function mapPvEcartErpLignesToTransfertUiLines(
  doc: Record<string, unknown> | null | undefined
): PvEcartTransfertUiLine[] {
  const out: PvEcartTransfertUiLine[] = [];
  for (const l of readPvEcartLignesFromErpDoc(doc)) {
    const mapped = mapErpLignesToPvRegularisationItems([l]);
    if (!mapped.length) {
      continue;
    }
    const it = mapped[0];
    const resolved = resolveLineSensAndMagnitude(it);
    if (!resolved || resolved.magnitude <= 0) {
      continue;
    }
    const signedQ =
      resolved.sens === 'EXCEDENT' ? -resolved.magnitude : resolved.magnitude;
    const ref =
      it.reference != null && String(it.reference).trim() !== ''
        ? String(it.reference).trim()
        : '-';
    const cb = String(it.code_barre ?? it.code_bar ?? '').trim();
    const pidRaw = l['product_id'];
    const pid = pidRaw != null ? Number(pidRaw) : NaN;
    out.push({
      designation: String(it.designation ?? '').trim(),
      quantity: signedQ,
      reference: ref,
      code_barre: cb,
      product_id: Number.isFinite(pid) && pid > 0 ? pid : null,
      description: l['description'] != null ? String(l['description']) : '',
    });
  }
  return out;
}

export function mapUiItemsToPvRegularisationItems(
  items: Array<{
    type?: string;
    designation?: string;
    description?: string;
    quantity?: number;
    qnt?: number;
    code_barre?: string;
    reference?: string;
    sens?: string;
  }> | null | undefined,
  descriptionByLineIndex?: (lineIndex: number) => string | undefined
): PvEcartDocumentItemForRegularisation[] {
  const list = (items || []).filter(
    (i) => i.type === 'product' && String(i.designation ?? '').trim()
  );
  return list.map((i, idx) => ({
    type: 'product',
    designation: String(i.designation).trim(),
    description:
      descriptionByLineIndex?.(idx) ??
      (i.description != null ? String(i.description) : undefined),
    quantity: Number(i.quantity ?? i.qnt) || 0,
    code_barre: i.code_barre,
    reference: i.reference,
    sens: i.sens,
  }));
}

/** Lignes ERP (approve-vente / API document) → items régularisation LC. */
export function mapErpLignesToPvRegularisationItems(
  lignes: Array<Record<string, unknown>> | null | undefined
): PvEcartDocumentItemForRegularisation[] {
  const out: PvEcartDocumentItemForRegularisation[] = [];
  for (const l of lignes || []) {
    const designation = String(l['libelle'] ?? l['designation'] ?? '').trim();
    if (!designation) {
      continue;
    }
    out.push({
      type: 'product',
      designation,
      description: l['description'] != null ? String(l['description']) : undefined,
      quantity: Number(l['qnt'] ?? l['quantity']) || 0,
      code_barre:
        l['code_barre'] != null
          ? String(l['code_barre'])
          : l['code_bar'] != null
            ? String(l['code_bar'])
            : undefined,
      reference:
        l['reference'] != null
          ? String(l['reference'])
          : l['ref_interne'] != null
            ? String(l['ref_interne'])
            : undefined,
      ref_fabricant: l['ref_fabricant'] != null ? String(l['ref_fabricant']) : undefined,
      sens: l['sens'] != null ? String(l['sens']) : undefined,
    });
  }
  return out;
}

/** PV workflow actif avec régularisation déjà appliquée (évite double stock BT). */
export function findPvEcartWithRegularisationApplied(
  children: Array<Record<string, unknown>> | null | undefined
): Record<string, unknown> | null {
  if (!Array.isArray(children) || !children.length) {
    return null;
  }
  const pvType = STOCK_TRANSFER_WORKFLOW_TYPE_IDS.PV_ECART;
  const candidates = children.filter((c) => Number(c['type_document_id']) === pvType);
  for (let i = candidates.length - 1; i >= 0; i--) {
    const doc = candidates[i];
    if (isRegularisationStockApplied(doc)) {
      return doc;
    }
  }
  return null;
}

function stockReferenceForPv(
  pvDoc: Record<string, unknown>,
  pvDocumentId: number
): string {
  const ref = String(pvDoc['ref_externe'] ?? pvDoc['reference'] ?? '').trim();
  return ref || `PV-ECART-REG-${pvDocumentId}`;
}

function defaultParametrageDocForPvPatch(): Record<string, unknown> {
  return {
    nbr_date: 0,
    aff_montant_ttc: false,
    aff_code_bar: false,
    aff_date_echeance: false,
    aff_date_liv: false,
    aff_montant_total: false,
    aff_coordonnees: false,
  };
}

function resolveParametrageDocForPvPatch(
  pvDoc: Record<string, unknown>
): Record<string, unknown> {
  const raw = pvDoc['parametrage_doc'];
  if (raw && typeof raw === 'object') {
    return { ...(raw as Record<string, unknown>) };
  }
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === 'object') {
        return { ...(parsed as Record<string, unknown>) };
      }
    } catch {
      /* ignore */
    }
  }
  return defaultParametrageDocForPvPatch();
}

/** PUT ERP minimal (champs obligatoires + hidden) sans toucher aux lignes. */
function buildPvEcartErpHiddenPatchPayload(
  pvDoc: Record<string, unknown>,
  hiddenClient: Record<string, unknown>
): Parameters<DocumentService['update']>[1] {
  const userId = Number(pvDoc['user_id'] ?? 0);
  const stationId = Number(pvDoc['station_id'] ?? 0);
  const typeId = Number(
    pvDoc['type_document_id'] ??
      (pvDoc['typeDocument'] as { id?: number } | undefined)?.id ??
      STOCK_TRANSFER_WORKFLOW_TYPE_IDS.PV_ECART
  );
  const statutId = Number(
    pvDoc['statut_id'] ?? (pvDoc['statut'] as { id?: number } | undefined)?.id ?? 0
  );
  const fmt = (key: string) => String(pvDoc[key] ?? '0.000');
  return {
    user_id: userId,
    station_id: stationId,
    type_document_id: typeId,
    statut_id: statutId,
    date: String(pvDoc['date'] ?? new Date().toISOString().slice(0, 10)),
    total_ht: fmt('total_ht'),
    total_tva: fmt('total_tva'),
    total_ttc: fmt('total_ttc'),
    timbre_fiscal: fmt('timbre_fiscal'),
    net_payer: fmt('net_payer'),
    parametrage_doc: resolveParametrageDocForPvPatch(pvDoc),
    hidden_client_fields: hiddenClient,
  } as unknown as Parameters<DocumentService['update']>[1];
}

/** Lie le PV ERP au transfert LC (sans marquer la régularisation stock). */
export function patchPvEcartLcTransfertLink$(
  documentService: DocumentService,
  pvDocumentId: number,
  pvDoc: Record<string, unknown>,
  lcTransfertId: number,
  lcNumDocument?: string | null
): Observable<void> {
  const existingHidden = readMergedDocumentHidden(pvDoc);
  const pvHidden: Record<string, unknown> = {
    ...existingHidden,
    pv_ecart_regularisation_lc_transfert_id: lcTransfertId,
    logiciel_caisse_transfert_id: lcTransfertId,
    stock_transfert_id: lcTransfertId,
  };
  const num = String(lcNumDocument ?? '').trim();
  if (num) {
    pvHidden['stock_transfert_num_document'] = num;
  }
  return documentService
    .update(
      pvDocumentId,
      buildPvEcartErpHiddenPatchPayload(pvDoc, pvHidden)
    )
    .pipe(map(() => undefined));
}

function isPvEcartRegularisationEndpointMissing(err: unknown): boolean {
  if (err && typeof err === 'object') {
    const status = Number((err as { status?: number }).status);
    if (status === 404) {
      return true;
    }
  }
  const msg = String(
    err instanceof Error ? err.message : (err as { message?: string })?.message ?? err
  );
  return /404|not found|introuvable|pv-ecart-regularisation/i.test(msg);
}

export function patchPvEcartRegularisationStockFlags$(
  documentService: DocumentService,
  pvDocumentId: number,
  pvDoc: Record<string, unknown>,
  stockReference: string,
  lcTransfertId: number,
  lcNumDocument?: string | null
): Observable<void> {
  const existingHidden = readMergedDocumentHidden(pvDoc);
  const pvHidden: Record<string, unknown> = {
    ...existingHidden,
    regularisation_stock_applied: true,
    regularisation_stock_reference: stockReference,
    pv_ecart_regularisation_lc_transfert_id: lcTransfertId,
    logiciel_caisse_transfert_id: lcTransfertId,
    stock_transfert_id: lcTransfertId,
  };
  const num = String(lcNumDocument ?? '').trim();
  if (num) {
    pvHidden['stock_transfert_num_document'] = num;
  }
  return documentService
    .update(
      pvDocumentId,
      buildPvEcartErpHiddenPatchPayload(pvDoc, pvHidden)
    )
    .pipe(map(() => undefined));
}

/** Payload PUT /api/stocks/pv-ecart-transfert-approuve-disponible (delta écart signé). */
export function buildPvEcartApprouveDisponiblePayload(params: {
  entrepotDepartLc: number;
  entrepotDestLc: number;
  referenceDocument: string;
  items: PvEcartDocumentItemForRegularisation[];
}): {
  entrepot_id_depart: number;
  reference_document: string;
  lignes: Array<{
    quantite: number;
    code_barre?: string | null;
    ref_interne?: string | null;
    ref_fabricant?: string | null;
    libelle?: string | null;
    product_id?: number | null;
  }>;
} | null {
  const ref = String(params.referenceDocument ?? '').trim();
  if (!ref) {
    return null;
  }
  const body = buildLcPvEcartTransfertBody(
    params.entrepotDepartLc,
    params.entrepotDestLc,
    params.items
  );
  if (!body?.lignes?.length) {
    return null;
  }
  const lignes: Array<{
    quantite: number;
    code_barre?: string | null;
    ref_interne?: string | null;
    ref_fabricant?: string | null;
    libelle?: string | null;
    product_id?: number | null;
  }> = [];
  for (const lg of body.lignes) {
    const libelle = String(lg.libelle_article ?? '').trim() || null;
    const refLine =
      lg.reference != null && String(lg.reference).trim() !== ''
        ? String(lg.reference).trim()
        : null;
    for (const alloc of lg.allocations || []) {
      const q = Math.trunc(Number(alloc.quantite) || 0);
      if (q === 0) {
        continue;
      }
      lignes.push({
        quantite: q,
        code_barre: alloc.code_barre ?? null,
        ref_interne: refLine,
        libelle,
        product_id: alloc.product_id ?? null,
      });
    }
  }
  if (!lignes.length) {
    return null;
  }
  return {
    entrepot_id_depart: params.entrepotDepartLc,
    reference_document: ref,
    lignes,
  };
}

/**
 * Approbation transfert PV : correction stock entrepôt initial selon l’écart (reçu − envoyé).
 * Écart > 0 (excédent) → baisse disponible ; écart < 0 (manquant) → hausse.
 */
export function buildPvEcartApprouveDisponiblePayloadFromEcartBreakdown(params: {
  entrepotDepartLc: number;
  referenceDocument: string;
  lines: Array<{
    ecartSigned: number;
    product_id?: number | null;
    code_barre?: string | null;
    reference?: string | null;
    designation?: string | null;
    ref_fabricant?: string | null;
  }>;
}): {
  entrepot_id_depart: number;
  reference_document: string;
  lignes: Array<{
    quantite: number;
    code_barre?: string | null;
    ref_interne?: string | null;
    ref_fabricant?: string | null;
    libelle?: string | null;
    product_id?: number | null;
  }>;
} | null {
  const ref = String(params.referenceDocument ?? '').trim();
  if (!ref) {
    return null;
  }
  const dep = Number(params.entrepotDepartLc);
  if (!Number.isFinite(dep) || dep <= 0) {
    return null;
  }
  const lignes: Array<{
    quantite: number;
    code_barre?: string | null;
    ref_interne?: string | null;
    ref_fabricant?: string | null;
    libelle?: string | null;
    product_id?: number | null;
  }> = [];
  for (const ln of params.lines || []) {
    const ecart = Math.trunc(Number(ln.ecartSigned) || 0);
    if (ecart === 0) {
      continue;
    }
    const cb = String(ln.code_barre ?? '').trim();
    const refLine = String(ln.reference ?? '').trim();
    const refFab = String(ln.ref_fabricant ?? '').trim();
    const pid = ln.product_id != null ? Number(ln.product_id) : NaN;
    if (
      !cb &&
      !refLine &&
      !refFab &&
      !(Number.isFinite(pid) && pid > 0) &&
      !String(ln.designation ?? '').trim()
    ) {
      continue;
    }
    lignes.push({
      quantite: ecart,
      code_barre: cb || null,
      ref_interne: refLine && refLine !== '-' ? refLine : null,
      ref_fabricant: refFab || null,
      libelle: ln.designation != null ? String(ln.designation).trim() || null : null,
      product_id: Number.isFinite(pid) && pid > 0 ? pid : null,
    });
  }
  if (!lignes.length) {
    return null;
  }
  return {
    entrepot_id_depart: dep,
    reference_document: ref,
    lignes,
  };
}

/**
 * PV « Validé » : ne crée plus de transfert LC (correction stock via transfert lié existant).
 */
export function applyPvEcartOnValide$(
  _lcStockTransfert: LogicielCaisseStockTransfertService,
  _lcStockReception: LogicielCaisseStockReceptionService,
  _documentService: DocumentService,
  params: {
    pvDocumentId: number;
    pvDoc: Record<string, unknown>;
    departLc: number;
    destLc: number;
    pvItems: PvEcartDocumentItemForRegularisation[];
    pvNote?: string | null;
  }
): Observable<ApplyPvEcartOnValideResult> {
  const { pvDoc } = params;
  const existingLcId = readPvEcartLcTransfertIdFromDoc(pvDoc);

  if (existingLcId != null) {
    const numHidden = String(
      readMergedDocumentHidden(pvDoc)['stock_transfert_num_document'] ?? ''
    ).trim();
    return of({
      lcTransfertId: existingLcId,
      lcNumDocument: numHidden || null,
      stockUpdated: 0,
      stockAlreadyApplied: true,
      stockReservationsOnTransfert: false,
    });
  }

  return throwError(
    () =>
      new Error(
        'Transfert entrepôt lié introuvable — ouvrez le transfert depuis Documents liés pour corriger le stock.'
      )
  );
}
